// Pure arithmetic for the meeting scoreboard. Kept free of React so the
// percentages and counts can be tested without rendering anything.

export const WON_STATUS = 'Deposit Collected (Won)';
const LOST_STATUS = 'Lost Deal';

const statusesOf = (deal) => (Array.isArray(deal?.statuses) ? deal.statuses : []);

export const isWon = (deal) => statusesOf(deal).includes(WON_STATUS);
const isLost = (deal) => statusesOf(deal).includes(LOST_STATUS);

// null (not 0) when there is nothing meaningful to compare, so callers can
// choose to render no bar at all rather than an empty one.
export function pct(actual, target) {
  const a = Number(actual), t = Number(target);
  if (actual == null || target == null || !Number.isFinite(a) || !Number.isFinite(t) || t === 0) return null;
  return (a / t) * 100;
}

export function dealMix(deals) {
  const list = deals || [];
  const won = list.filter(isWon).length;
  const lost = list.filter(d => !isWon(d) && isLost(d)).length;
  return { won, lost, open: list.length - won - lost, total: list.length };
}

export function weightPct(ticket, max) {
  const t = Number(ticket), m = Number(max);
  if (!Number.isFinite(t) || !Number.isFinite(m) || m <= 0) return 0;
  return (t / m) * 100;
}

export const sumTickets = (deals) =>
  (deals || []).reduce((n, d) => n + (Number(d.ticket_size) || 0), 0);

// Booking-tracker salespeople (JQ, Zen) have their total replaced by booking
// figures. `delivered` is the analogue of a plain person's `actual` — both are
// revenue realised this month. `captured` is what was BOOKED this month and is
// routinely zero, so ranking on it reports a productive person as having earned
// nothing.
export const revenueOf = (sec) => {
  const t = sec?.checkins?.total || {};
  if (t.actual != null) return t.actual;
  // Current booking sections always carry `delivered`, even when Bukku has no
  // amount yet. In that case captured must not masquerade as delivered.
  if (Object.prototype.hasOwnProperty.call(t, 'delivered')) return t.delivered;
  return t.captured != null ? t.captured : null;
};
export const apoOf = (sec) => {
  const c = sec?.checkins || {};
  if (c.apo?.actual != null) return c.apo.actual;
  const t = c.total || {};
  if (t.deliveredApo != null) return t.deliveredApo;
  return t.capturedApo != null ? t.capturedApo : null;
};

// Booking-tracker salespeople (JQ, Zen) additionally carry a `captured`
// figure (what was BOOKED this month) alongside `delivered`. The league and
// the primary revenue tile rank/show delivered only (see revenueOf above),
// but the scoreboard restores captured as a second figure so JQ/Zen don't
// lose information they relied on in the old design. `captured` is routinely
// 0, so this must use `!= null`, never truthiness, or a real zero would be
// reported as missing.
export const capturedOf = (sec) => {
  const t = sec?.checkins?.total;
  return t?.captured != null ? t.captured : null;
};

// Captured revenue exists for everyone in the meeting scorecard, but the shape
// differs by age/source: current sections carry the shared scorecardFigures
// value, older frozen meetings may only carry scorecardView, and booking-tracker
// sections keep the full tracker total on checkins.total. Prefer that total for
// JQ/Zen, then use the venue scorecard for everyone else. A real zero is known.
export const capturedRevenueOf = (sec) => {
  // The booking tracker includes every venue. The venue scorecard deliberately
  // excludes unmapped venues, so its captured subtotal cannot be the target actual.
  if (sec?.checkins?.total?.source === 'booking' && sec.checkins.total.captured != null)
    return sec.checkins.total.captured;
  if (sec?.scorecardFigures?.captured != null) return sec.scorecardFigures.captured;
  if (sec?.scorecardView?.capturedRevActual != null) return sec.scorecardView.capturedRevActual;
  return capturedOf(sec);
};

export const ytdDeliveredOf = (sec) => {
  const total = sec?.checkins?.total;
  return total?.ytdDelivered != null ? total.ytdDelivered : null;
};

const sumKnown = (sections, read) => {
  const values = (sections || [])
    .map(read)
    .filter(v => v != null && Number.isFinite(Number(v)))
    .map(Number);
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
};

// One pair of whole-team figures for the meeting overview. These are sums of
// the same per-person values shown below, not a second accounting definition.
export const teamRevenueTotals = (sections) => ({
  captured: sumKnown(sections, capturedRevenueOf),
  delivered: sumKnown(sections, revenueOf),
  ytdDelivered: sumKnown(sections, ytdDeliveredOf),
});

// Zen's booking tracker further splits captured into hunting vs. lead-gen.
// Plain salespeople and JQ (no split) both resolve to null here.
export const capturedSplitOf = (sec) => {
  const t = sec?.checkins?.total;
  return t?.split != null ? t.split : null;
};

// The month's enquiry count from the lead records, every project, junk excluded --
// the same number the Lead Report shows, so the tile can never disagree with it.
// The server carries it on `scorecardFigures.enquiries` (scorecardFigures.js, rule 5);
// `scorecardView.enquiryActual` is the same count on a meeting frozen before
// scorecardFigures existed. The typed weekly box in `checkins.scorecard.enqWeek` is
// deliberately NOT read: enquiries are synced, not typed, so nobody fills it and it
// rendered every tile as an em dash. A counted 0 is 0, so `!= null`, never truthiness.
export const enquiriesOf = (sec) => {
  const f = sec?.scorecardFigures;
  if (f?.enquiries != null) return f.enquiries;
  const v = sec?.scorecardView;
  return v?.enquiryActual != null ? v.enquiryActual : null;
};

// Only deals the cron actually copied carry provenance; hand-added deals have
// null. The banner's count must never include those, or the page claims a
// carry-forward that did not happen.
export const carriedIn = (sec) =>
  (sec?.deals || []).filter(d => d.carried_from_meeting_id != null).length;

// Shared by the person card, the jump rail and the "now viewing" strip so
// all three agree on the same avatar initials for a given name.
export const initials = (name) => String(name || '').trim().slice(0, 2).toUpperCase();

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// `as_of` is a 'YYYY-MM-DD' string. Formatted by hand (not Date/toLocaleDateString)
// so a date-only string never gets shifted a day by the browser's local timezone.
// Returns null on anything that doesn't parse, so the caller can fall back rather
// than print a broken date.
function formatAsOf(asOf) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(asOf));
  if (!m) return null;
  const month = SHORT_MONTHS[parseInt(m[2], 10) - 1];
  const day = parseInt(m[3], 10);
  if (!month || !Number.isFinite(day)) return null;
  return `${day} ${month}`;
}

// The meeting chrome, beside the date: which basis the figures on screen are on.
// `frozen` is `{ as_of, source } | null` — a section's `frozen` field from
// GET /api/meetings/:id (every section in a meeting agrees, since a mixed meeting
// always computes live and reports null throughout).
//
// `as_of` can be null even when `frozen` itself isn't: a malformed or legacy
// record, tolerated defensively here rather than assumed away. A frozen meeting
// with no date reads as "Figures frozen" — stating what's true (the figures are
// fixed) without fabricating a date or falling silent.
export function frozenLabel(frozen) {
  if (!frozen) return 'Figures update live';
  const date = frozen.as_of != null ? formatAsOf(frozen.as_of) : null;
  const base = date ? `Figures as of ${date}` : 'Figures frozen';
  return frozen.source === 'rebuilt' ? `${base} · rebuilt from invoice history` : base;
}

// The week a deal closed, for the closed-deals list: 'wk of 4 Sep'. Empty for a
// date that does not parse, so a bad value prints nothing rather than 'wk of null'.
export function weekLabel(dateStr) {
  const d = formatAsOf(dateStr);
  return d ? `wk of ${d}` : '';
}

// 'YYYY-MM' moved by `delta` months, done on the digits so no Date/timezone can
// slide the month. Anything that does not parse is returned unchanged.
export function shiftMonth(month, delta) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month ?? ''));
  if (!m) return month;
  const idx = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta;
  const y = Math.floor(idx / 12), mo = idx - y * 12 + 1;
  return `${y}-${String(mo).padStart(2, '0')}`;
}

// 'YYYY-MM' -> 'Sep 2026'; '' when it does not parse.
export function monthTitle(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month ?? ''));
  if (!m) return '';
  const name = SHORT_MONTHS[Number(m[2]) - 1];
  return name ? `${name} ${m[1]}` : '';
}

// Human-readable time bases for the scan-first meeting summary. Meeting dates
// are date-only strings, so this intentionally avoids Date parsing/timezones.
// A meeting scheduled after today reports through today; a past meeting reports
// through its own date. Supplying `today` keeps the helper deterministic in tests.
export function meetingPeriodLabels(meetingDate, today = new Date()) {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(meetingDate ?? ''));
  if (!parsed) return { month: '', ytd: '1 Jan to meeting date' };

  const meetingIso = `${parsed[1]}-${parsed[2]}-${parsed[3]}`;
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const asOfIso = meetingIso.slice(0, 4) === todayIso.slice(0, 4) && meetingIso > todayIso
    ? todayIso
    : meetingIso;
  const asOf = /^(\d{4})-(\d{2})-(\d{2})$/.exec(asOfIso);
  const monthName = LONG_MONTHS[Number(parsed[2]) - 1];
  const shortMonth = SHORT_MONTHS[Number(asOf[2]) - 1];

  return {
    month: monthName ? `${monthName} ${parsed[1]}` : '',
    ytd: `1 Jan–${Number(asOf[3])} ${shortMonth} ${asOf[1]}`,
  };
}

export const countCarried = (sections) =>
  (sections || []).reduce((n, s) => n + carriedIn(s), 0);
